import { Component, ComponentInterface, Element, JSX, Listen, Prop, State, h } from '@stencil/core';
import { ProductCategory as ProductCategoryEnum } from '@aglenergy/shared-library';
// eslint-disable-next-line import/no-unresolved
import { ModalCloseAction } from '@aglenergy/design-system/dist/types/components/core/modal/modal/modal.types';
import { NbnProductsResponse, isNbnAddressSuccessResponse } from 'app/nbn/models/nbn-products-response.model';
import { store } from 'app/shared/store/store';
import { initStore } from 'app/shared/store/init-store';

import {
  AddressDetailModel,
  ContentMode,
  Context,
  DataSpaKey,
  ErrorMessage,
  ProductCategory,
  ProductsResponse,
  ResponseError
} from 'app/shared/models/index';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { WindowMessageType } from 'app/shared/constants/window-message-type.enum';
import { addressService } from 'app/shared/services/address/address.service';
import { bundleService } from 'app/shared/services/bundle/bundle.service';
import { configService } from 'app/shared/services/config/config.service';
import { errorMessageService } from 'app/shared/services/error-message/error-message.service';
import { flowService } from 'app/shared/services/flow/flow-service';
import { getDataSpaSearchParams } from 'app/shared/utilities/helper-functions';
import { nbnProductSearchApiService } from 'app/nbn/services/nbn-product-search-api/nbn-product-search-api.service';
import { nbnProductService } from 'app/nbn/services/nbn-product/nbn-product.service';
import { productService } from 'app/shared/services/productService/product.service';
import { renderTechTypeMessage } from 'app/nbn/components/nbn-technology-type-message/nbn-technology-type-message';
import { scrollToElement } from 'utils/dom.helper';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';
import { ErrorModal } from 'app/shared/components/error-modal/error-modal';
import { onGuidedFlowLoad } from 'app/bundle/store/actions/cartActions';
import { openApiService } from 'app/shared/services/open-api/open-api.service';
import { productsSessionStorageService } from 'app/shared/services';

@Component({
  tag: 'app-address-search',
  styleUrl: 'address-search.scss'
})
export class AddressSearch implements ComponentInterface {
  @Prop() public readonly dataSpa: string = null;
  @Prop() public readonly searchTitle: string = null;

  private aglDsAddressSearchRef: any;
  private manualRef: any;
  private enableLoginCta = false;
  private errorModalRef: ErrorModal;
  private hasError = false;
  private isAddressCheck = false;
  private isGuidedFlow = false;
  private aemSearchTitle: string = null;
  private placeholder: string = null;
  private alignment: string = null;
  private autoSelectedAddress: string = null;
  private manualAddressCloseAction: ModalCloseAction;
  private aemCIFIntegration: boolean;
  private isMoversFlow = false;
  private isInternetPlansPage = false;

  @State() private locationId: string = null;
  @State() private isNbnMovers = false;

  private get productCategory(): ProductCategory {
    return store.get(StoreKeys.productCategory);
  }
  private get productCategoryText(): string {
    const productText = this.productCategory === 'nbn' ? 'internet' : this.productCategory;
    return productText ? productText : '';
  }

  @Element() private el: HTMLAglDsAddressSearchElement;

  public async componentWillLoad(): Promise<void> {
    const dataSpaParamValue = getDataSpaSearchParams(this.dataSpa);
    this.isGuidedFlow = dataSpaParamValue?.getBoolean(DataSpaKey.isGuidedFlow);
    this.enableLoginCta = dataSpaParamValue?.getBoolean(DataSpaKey.enableLoginCta);
    this.aemSearchTitle = dataSpaParamValue?.get(DataSpaKey.addressBarTitle);
    this.placeholder = dataSpaParamValue?.get(DataSpaKey.addressBarPlaceholder);
    this.alignment = dataSpaParamValue?.get(DataSpaKey.addressBarAlignment);
    this.aemCIFIntegration = dataSpaParamValue?.getBoolean(DataSpaKey.aemCIFIntegration);
    store.set(StoreKeys.aemCIFIntegrationEnabled, this.aemCIFIntegration);
    this.locationId = new URLSearchParams(window.location.search.toUpperCase()).get('LOCID');
    const isCampaignPage = dataSpaParamValue?.getBoolean(DataSpaKey.isCampaignPage);
    store.set(StoreKeys.isCampaignPage, isCampaignPage);
    const webIDForCampaign = new URLSearchParams(window.location.search.toUpperCase()).get('WEBID');
    const mode = dataSpaParamValue?.get(DataSpaKey.mode);
    const contentMode = dataSpaParamValue?.get(DataSpaKey.mode) as ContentMode;
    store.set('contentMode', contentMode ?? 'publish'); // setting this earlier before initStore as configService.loadConfig needs it for the general content call

    let host = '';
    if (mode === 'publish') {
      host = '/apps/comparison';
    } else {
      host = dataSpaParamValue?.get(DataSpaKey.host);
    }

    const previousWebId = productsSessionStorageService.getLatestWebIdFromProducts();

    if (isCampaignPage) {
      store.set(StoreKeys.webId, webIDForCampaign);

      if (previousWebId?.toUpperCase() !== webIDForCampaign?.toUpperCase()) {
        productsSessionStorageService.removeProducts();
      }
    } else if (previousWebId) {
      productsSessionStorageService.removeProducts();
    }

    this.isMoversFlow = sessionStorage.getItem('appConfig') === 'movers';

    if (dataSpaParamValue) {
      this.isNbnMovers = dataSpaParamValue.get(DataSpaKey.isNbnMovers) === 'true';
      this.isInternetPlansPage = dataSpaParamValue.get(DataSpaKey.isInternetPlansPage) === 'true';
    }

    if (this.locationId) {
      return configService.loadConfig('/apps/comparison').then(() => {
        store.set(StoreKeys.prefillAddress, true);
        tealiumAnalyticService.trackPrefillAddress('Started', 'Prefill Address from LocId', ProductCategoryEnum.Nbn);
        return nbnProductSearchApiService.getNbnAddress(this.locationId).then((res) => {
          if (isNbnAddressSuccessResponse(res)) {
            this.autoSelectedAddress = res.addressLong;
            tealiumAnalyticService.trackPrefillAddress(
              'Address Validated',
              'Prefill Address Validated Successfully',
              ProductCategoryEnum.Nbn
            );
          } else {
            store.set(StoreKeys.prefillAddress, false);
            tealiumAnalyticService.sendAnalyticsError('[BCP][Search][Address][Prepopulated][Error]', res.message, res.code, 'API');
          }
        });
      });
    }

    if (this.isGuidedFlow) {
      store.set('isGuidedFlow', this.isGuidedFlow);
      const couponCode = dataSpaParamValue.get(DataSpaKey.couponCode);
      const nbnCouponCode = dataSpaParamValue.get(DataSpaKey.nbnCouponCode);
      const mobileCouponCode = dataSpaParamValue.get(DataSpaKey.mobileCouponCode);
      if (couponCode) {
        store.set('couponCode', couponCode);
      }
      if (mobileCouponCode) {
        store.set('couponCodes', [...store.get('couponCodes'), { couponCode: mobileCouponCode, productCategory: 'mobile plan' }]);
      }
      if (nbnCouponCode) {
        store.set('couponCodes', [...store.get('couponCodes'), { couponCode: nbnCouponCode, productCategory: 'nbn' }]);
      }
      await configService.loadConfig(host).then(async () => {
        await initStore(this.enableLoginCta, this.isGuidedFlow, null, contentMode, null, host).then(() => {
          openApiService.setBaseUrl();
          onGuidedFlowLoad();
        });
      });
    }

    if (this.aemCIFIntegration) {
      const productCategory = dataSpaParamValue.get(DataSpaKey.productcategory) as ProductCategory;
      store.onChange('productDetails', (productDetails) => {
        this.sendProductsToAEM(productDetails, store.get('address'));
      });
      await configService.loadConfig(host).then(async () => {
        await initStore(false, false, productCategory, contentMode, null, host);
      });

      window.postMessage(
        {
          spaCategory: store.get(StoreKeys.productCategory),
          eventType: 'spa-loaded',
          eventData: {
            isAddressEntered: store.get('address') ? addressService.convertAddressToString(store.get('address'))?.length > 0 : false
          }
        },
        '*'
      );

      window.addEventListener('message', (event) => {
        const selectedProductFromAEM = event.data;
        const productDetails = store.get(StoreKeys.productDetails);
        if (selectedProductFromAEM.type === WindowMessageType.SelectedProductFromAEM && productDetails?.products.length > 0) {
          productService.onSelectedProductForNbn(productDetails.products, store.get('address'), event.data.data);
        }
      });
    }
  }

  private checkValidationError = (errors: Error[]): void => {
    this.hasError = false;
    store.set('showValidIcon', false);

    if (this.aglDsAddressSearchRef) {
      const addressSearch = this.aglDsAddressSearchRef;
      const inputAddress: HTMLFormElement = addressSearch.querySelector('input');

      if (errors && errors.length > 0) {
        const error = errors.filter((e) => e.name === 'address');
        if (error) {
          this.hasError = true;
          setTimeout(() => {
            inputAddress.focus();
          }, 1000);
          scrollToElement(addressSearch as HTMLElement, 50);
        }
      } else if (store.get('address')) {
        store.set('showValidIcon', true);
      }
    }
  };

  private checkResponseError = (responseError: ResponseError, productCategory?: ProductCategory): void => {
    if (responseError?.errorType === 'aborted') {
      return;
    }
    const flowType = flowService.getFlowTypeFromStore();
    if (responseError?.errorType === 'serviceNotAvailable') {
      const errorMessageData = errorMessageService.getErrorMessageData(responseError?.errorType, productCategory, flowType);
      this.showErrorModal(errorMessageData);
      tealiumAnalyticService.sendAnalyticsError('[BCP][Product Cards][Error]', '', 'serviceNotAvailable', 'API');
    } else if (responseError?.errorType === 'multipleMatching') {
      this.manualRef.openModal(null);

      tealiumAnalyticService.sendAnalyticsError('[BCP][Product Cards][Error]', '', responseError.message, 'API');
    } else if ((responseError || responseError?.errorType === 'systemError') && this.isAddressCheck) {
      if (!this.isGuidedFlow) {
        const errorMessageData = errorMessageService.getErrorMessageData('systemError', productCategory, flowType);
        this.showErrorModal(errorMessageData);
      }
      tealiumAnalyticService.sendAnalyticsError('[BCP][Product Cards][Error]', '', responseError.message, 'API');
    }
  };

  private checkSiteError(productDetails: ProductsResponse, productCategory: ProductCategory): void {
    if (productCategory !== 'energy') {
      if (productDetails?.siteError?.reasonCode === 'NotFound') {
        const flowType = flowService.getFlowTypeFromStore();
        const errorMessageData = errorMessageService.getErrorMessageData('serviceNotAvailable', productCategory, flowType);
        this.showErrorModal(errorMessageData);
        this.hasError = true;
      } else if (productDetails?.siteError?.reasonCode === 'MultipleChoices') {
        this.manualRef.openModal(null);
      }
    }
  }

  private setAddressString(addressDetailModel: AddressDetailModel): string {
    let addressString: string;
    if (addressDetailModel.poBoxType) {
      const poBoxNumber =
        addressDetailModel.poBoxNumber !== null && addressDetailModel.poBoxNumber !== undefined ? addressDetailModel.poBoxNumber : '';
      addressString =
        `${addressDetailModel.poBoxType} ${poBoxNumber}, ${addressDetailModel.suburb} ${addressDetailModel.state} ${addressDetailModel.postcode}`.trimLeft();
    } else {
      const unitNumber =
        addressDetailModel.unitNumber !== null && addressDetailModel.unitNumber !== undefined ? addressDetailModel.unitNumber : '';
      addressString =
        `${unitNumber} ${addressDetailModel.streetNumber} ${addressDetailModel.streetName} ${addressDetailModel.streetType}, ${addressDetailModel.suburb} ${addressDetailModel.state} ${addressDetailModel.postcode}`.trimLeft();
    }
    return addressString;
  }

  @Listen('addressSearchInput')
  public onAddressChange(): void {
    store.set('showValidIcon', false);
  }

  private onManualAddressModalClose() {
    const manualAddressModalElement = document.querySelector('#manual-entry-modal');
    if (manualAddressModalElement) {
      manualAddressModalElement.addEventListener('modalClosed', () => {
        if (this.manualAddressCloseAction !== 'manual') {
          this.aglDsAddressSearchRef.setTextBoxValue(null);
          this.isAddressCheck = false;
          store.set('address', null);
          store.set('showValidIcon', false);
        }
      });
    }
  }

  private setAddressValue = (addressValue: string): void => {
    tealiumAnalyticService.createAndSendAddressData(['Started'], '', '', store.get(StoreKeys.productCategory));
    if (this.aglDsAddressSearchRef?.setTextBoxValue) {
      this.aglDsAddressSearchRef?.setTextBoxValue(addressValue);
    }
    this.isAddressCheck = true;
  };

  private getNbnMoversSearchTitle(): string {
    if (this.isMoversFlow) {
      return 'Your new address';
    }
    return null;
  }

  public showErrorModal(errorMessageData: ErrorMessage): void {
    this.errorModalRef.errorMessageData = errorMessageData;
    this.errorModalRef.showModal(this.el);
    this.hasError = true;
    tealiumAnalyticService.sendAnalyticsError('[Bundle Builder][Error Popup][Shown]', null, errorMessageData.errorMessage, 'Inline');
  }

  public getValidationText(productCategory: ProductCategory, productDetails: ProductsResponse, responseError: ResponseError): string {
    if (productCategory === 'nbn') {
      const defaultAddrNoService = nbnProductService.hasNoService(productDetails as NbnProductsResponse);
      const selectedAddrNoService = nbnProductService.isServiceNotAvailable(responseError);
      return selectedAddrNoService || defaultAddrNoService ? '' : 'No address selected.';
    }
    return '';
  }

  public runProductValidators(productCategory: ProductCategory, productDetails: ProductsResponse): void {
    if (productCategory === 'nbn') {
      if (nbnProductService.hasNoService(productDetails as NbnProductsResponse)) {
        const errorMessageData = errorMessageService.getErrorMessageData(
          'serviceNotAvailable',
          productCategory,
          flowService.getFlowTypeFromStore()
        );
        errorMessageData.showFooter = false;
        this.showErrorModal(errorMessageData);
      }
    }
  }

  public showAddress(): boolean {
    if (this.productCategory === 'nbn' || this.isGuidedFlow) {
      return true;
    } else if (bundleService.isExplicitBundleFlow()) {
      return bundleService.isShowAddress();
    }
    return false;
  }

  public showNbnSqMessage(nbnProductDetails: NbnProductsResponse, address: string, isGuidedFlow: boolean): JSX.Element {
    const delayMessage = store.get('delayMessage');

    if (isGuidedFlow) {
      return null;
    }

    if (this.isInternetPlansPage && (!nbnProductDetails || !nbnProductDetails.serviceQualification)) return null;

    return (
      <div class="address-search-techtype__container">
        {renderTechTypeMessage(nbnProductDetails, address, false, delayMessage, false, null, isGuidedFlow)}
      </div>
    );
  }

  public sendProductsToAEM(productDetails: ProductsResponse, addressValue: AddressDetailModel): void {
    if (addressValue && this.aemCIFIntegration) {
      if (!productDetails?.products?.length) {
        return;
      }
      const planCategoriesForAEM = productDetails?.planCategories;
      const productsForAEM = productDetails?.products?.map((p) => ({
        // eslint-disable-next-line no-nested-ternary
        categories: p?.content === null ? [p?.fibreUpgrade === false ? 'nbn plans' : 'Fibre upgrade'] : p?.content?.planCategories,
        sku: p?.sku,
        fibreUpgrade: p?.fibreUpgrade,
        qualified: p?.qualified
      }));
      window.postMessage(
        {
          spaCategory: store.get('productCategory'),
          eventType: 'products-loaded',
          eventData: {
            planCategories: planCategoriesForAEM,
            products: productsForAEM
          }
        },
        '*'
      );
    }
  }

  private clearNbnMoversStoredData(): void {
    let customerProfile = JSON.parse(sessionStorage.getItem('customerProfileService'));
    let questionResponseMovers = JSON.parse(sessionStorage.getItem('questionResponseMovers'));

    customerProfile = {
      ...customerProfile,
      isGasUpdated: false,
      isElectricityUpdated: false,
      isInternetUpdated: false,
      isMobileUpdated: false
    };
    if (questionResponseMovers) {
      questionResponseMovers = questionResponseMovers.filter((question) => question.name === 'movers-new-existing');
    }

    sessionStorage.setItem('customerProfileService', JSON.stringify(customerProfile));
    sessionStorage.setItem('questionResponseMovers', JSON.stringify(questionResponseMovers));
  }

  private handleManualAddressEntryModalClosedForCIF(forceRefresh: boolean): void {
    if (forceRefresh) {
      window.postMessage(
        {
          eventType: 'modal-closed',
          spaCategory: store.get('productCategory')
        },
        '*'
      );
      this.forceRefreshPage();
    }
  }

  private forceRefreshPage() {
    window.location.reload();
  }

  private renderFunc = ({
    configData,
    onAddressChange,
    validationErrors,
    responseError,
    selectedAddress,
    productDetails,
    isPersonalisedProductsLoading,
    productCategory,
    showValidIcon
  }: Context): JSX.Element => {
    this.checkValidationError(validationErrors);
    if (selectedAddress) {
      this.isAddressCheck = true;
    }
    this.checkSiteError(productDetails, productCategory);
    this.checkResponseError(responseError, productCategory);
    this.runProductValidators(productCategory, productDetails);
    const isGuidedFlow = store.get(StoreKeys.isGuidedFlow);
    let selectedAddressString = selectedAddress ? addressService.convertAddressToString(selectedAddress) : this.autoSelectedAddress;
    let addressSearchClass = 'address-search';
    const addressSearchLabelId = 'address-search-label';
    this.onManualAddressModalClose();
    if (this.alignment) {
      addressSearchClass = `${addressSearchClass} ${this.alignment}`;
    }
    return !this.isNbnMovers ? (
      <host>
        {this.showAddress() && (
          <div class={addressSearchClass}>
            <div class="address-search__container">
              <label class="address-search__title" htmlFor={`${addressSearchLabelId}_textBox`}>
                {this.getNbnMoversSearchTitle() ??
                  this.aemSearchTitle ??
                  this.searchTitle ??
                  `Show me ${this.productCategoryText} plans for`}
              </label>
              <div class="address-search__input">
                {configData && (
                  <agl-ds-address-search
                    data-testid="agl-ds-address-search"
                    labelId={addressSearchLabelId}
                    ref={(el) => (this.aglDsAddressSearchRef = el)}
                    type="experiential"
                    placeholder={this.placeholder ?? 'e.g. 123 Sample Street, Melbourne'}
                    debounce-time="250"
                    base-url={configData.addressApiUrl}
                    subscription-key={configData.bffOcpApimSubscriptionKey}
                    manual-address-heading="What address do you want to connect"
                    validation-text={this.getValidationText(productCategory, productDetails, responseError)}
                    has-error={this.hasError}
                    // eslint-disable-next-line react/jsx-no-bind
                    onAddressSelected={(event: CustomEvent<AddressDetailModel>) => onAddressChange(event.detail)}
                    validSelectionMode="external"
                    showValidIcon={!isPersonalisedProductsLoading && showValidIcon}
                    address-input-value={selectedAddressString}
                    address-input-auto-select={this.autoSelectedAddress !== null}
                    showSpinner={isPersonalisedProductsLoading}
                    // eslint-disable-next-line react/jsx-no-bind
                    onAddressSearchBlur={() => this.setAddressValue(selectedAddressString)}
                  />
                )}
              </div>
              <div class="address-search__manual">
                <agl-ds-manual-address-entry
                  id="manual-entry-modal"
                  ref={(element) => (this.manualRef = element)}
                  heading="We found a few matches. Please enter your address."
                  default-address-detail={selectedAddressString}
                  // eslint-disable-next-line react/jsx-no-bind
                  onAddressConfirm={(e) => {
                    selectedAddressString = this.setAddressString(e.detail);
                    this.setAddressValue(selectedAddressString);
                    onAddressChange(e.detail);
                  }}
                  // eslint-disable-next-line react/jsx-no-bind
                  onAddressCloseAction={(e) => {
                    this.manualAddressCloseAction = e.detail as ModalCloseAction;
                    if (
                      store.get(StoreKeys.aemCIFIntegrationEnabled) &&
                      e.type === 'addressCloseAction' &&
                      (e.detail === 'clickOutside' || e.detail === 'iconButton' || e.detail === 'manualReturn')
                    ) {
                      this.handleManualAddressEntryModalClosedForCIF(true);
                    }
                  }}
                />
              </div>
            </div>
            {this.enableLoginCta && this.showLoginToMyAccount()}
            {this.productCategory === ProductCategoryEnum.Nbn &&
              this.showNbnSqMessage(productDetails as NbnProductsResponse, selectedAddressString, isGuidedFlow)}
          </div>
        )}
      </host>
    ) : (
      <host>
        {this.showAddress() && (
          <div class="address-search__manual">
            <agl-ds-manual-address-entry
              id="manual-entry-modal"
              ref={(element) => (this.manualRef = element)}
              heading="We found a few matches. Please enter your address."
              default-address-detail={selectedAddressString}
              // eslint-disable-next-line react/jsx-no-bind
              onAddressConfirm={(e) => {
                selectedAddressString = this.setAddressString(e.detail);
                this.setAddressValue(selectedAddressString);
                onAddressChange(e.detail);
              }}
              // eslint-disable-next-line react/jsx-no-bind
              onAddressCloseAction={(e) => {
                this.manualAddressCloseAction = e.detail as ModalCloseAction;
                if (
                  e.type === 'addressCloseAction' &&
                  (e.detail === 'clickOutside' || e.detail === 'iconButton' || e.detail === 'manualReturn')
                ) {
                  this.clearNbnMoversStoredData();
                  window.location.href = '/apps/confirm/?#/movers/address';
                }
              }}
            />
          </div>
        )}
      </host>
    );
  };

  public render(): JSX.Element {
    const errorMessageData = errorMessageService.getErrorMessageData('serviceNotAvailable', null, flowService.getFlowTypeFromStore());

    return (
      <div>
        <error-modal data-testid="error-modal" ref={(el) => (this.errorModalRef = el)} errorMessageData={errorMessageData} />
        <product-comparison-container renderFunc={this.renderFunc} />
      </div>
    );
  }

  private showLoginToMyAccount(): JSX.Element {
    return <login-to-account />;
  }
}
